/**
 * Format API / axios errors into a user-facing string for AlertModal.
 * Handles FastAPI string detail, 422 validation arrays, and network failures.
 */

const FIELD_LABELS = {
  title: 'Title',
  description: 'Description',
  url: 'URL',
  imageUrl: 'Image URL',
  toolType: 'Tool type',
  appSubtype: 'App subtype',
  rcAppUrl: 'RC App URL',
  steps: 'Steps',
  status: 'Status',
  persona: 'Primary persona',
  personas: 'Personas',
  makerEmail: 'Maker email',
  makerEmails: 'Maker emails',
  makers: 'Makers',
  roles: 'Roles',
  journeys: 'Journeys',
  tags: 'Tags',
  capabilities: 'Capabilities',
  environment: 'Environment',
  model: 'Model',
  userEmail: 'User email',
  email: 'Email',
  displayName: 'Display name',
  role: 'Role',
  gitUrl: 'Git URL',
  defaultRef: 'Branch',
  llmProvider: 'Agent',
  body: null,
};

function humanizeField(locPart) {
  if (locPart == null || locPart === 'body' || locPart === 'query' || locPart === 'path') {
    return null;
  }
  if (typeof locPart === 'number') {
    return `Item ${locPart + 1}`;
  }
  const key = String(locPart);
  if (FIELD_LABELS[key] !== undefined) {
    return FIELD_LABELS[key];
  }
  // camelCase / snake_case → Title Case
  return key
    .replace(/_/g, ' ')
    .replace(/([A-Z])/g, ' $1')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^./, (c) => c.toUpperCase());
}

function formatValidationItem(item) {
  if (!item || typeof item !== 'object') {
    return String(item);
  }
  const loc = Array.isArray(item.loc) ? item.loc : [];
  const fieldParts = loc.map(humanizeField).filter(Boolean);
  const field = fieldParts.length ? fieldParts.join(' → ') : null;
  const msg = item.msg || item.message || 'Invalid value';
  return field ? `${field}: ${msg}` : msg;
}

function formatDetail(detail) {
  if (detail == null) return null;
  if (typeof detail === 'string') {
    const trimmed = detail.trim();
    if (!trimmed) return null;
    // FastAPI default unhandled exception
    if (/^internal server error$/i.test(trimmed)) {
      return null;
    }
    return trimmed;
  }
  if (Array.isArray(detail)) {
    const lines = detail.map(formatValidationItem).filter(Boolean);
    return lines.length ? lines.join('\n') : null;
  }
  if (typeof detail === 'object') {
    if (typeof detail.message === 'string') return detail.message;
    if (typeof detail.error === 'string') return detail.error;
    if (typeof detail.detail === 'string') return detail.detail;
    try {
      return JSON.stringify(detail);
    } catch {
      return null;
    }
  }
  return String(detail);
}

/**
 * @param {unknown} error - axios / Error
 * @param {string} [fallback='Something went wrong. Please try again.']
 * @returns {string}
 */
export function formatApiError(error, fallback = 'Something went wrong. Please try again.') {
  if (!error) return fallback;

  const status = error.response?.status;
  const data = error.response?.data;
  const fromDetail = formatDetail(data?.detail);
  if (fromDetail) return fromDetail;

  // Other common body shapes
  const fromMessage = formatDetail(data?.message);
  if (fromMessage) return fromMessage;
  const fromError = formatDetail(data?.error);
  if (fromError) return fromError;

  if (!error.response) {
    if (error.code === 'ECONNABORTED') {
      return 'Request timed out. Please try again.';
    }
    if (error.message && /network error/i.test(error.message)) {
      return 'Could not reach the API. Check that the backend is running.';
    }
    return error.message || fallback;
  }

  if (status === 400) return 'Invalid request. Please check the form fields and try again.';
  if (status === 403) return 'You do not have permission to do that.';
  if (status === 404) return 'The requested item was not found.';
  if (status === 409) return 'Conflict: this item already exists or cannot be updated that way.';
  if (status === 422) return 'Validation failed. Please check required fields.';
  if (status >= 500) {
    return 'Server error while saving. Please try again or check backend logs.';
  }

  // Avoid raw axios "Request failed with status code 500"
  if (error.message && !/^Request failed with status code \d+$/i.test(error.message)) {
    return error.message;
  }

  return fallback;
}

export default formatApiError;
